import { CollectionLayout } from 'components/layouts/CollectionLayout';
import { Wrapper } from 'components/theme/Wrapper';
import React, { useEffect } from 'react';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { useIsTablet } from 'WindowDimensionProvider';
import { PageTitle } from 'components/atoms/PageTitle';
import { ANALYTICS_PROPERTY } from '@kouto/types';
import { useAppState } from 'AppProvider';
import { analyticsManager } from 'features/analytics';
import NotFoundExperience from 'components/NotFoundExperience/NotFoundExperience';
import { isAtRoute } from 'router/routeMatch';
import { TwoColumnMapContainer } from './components';
import useResourceBookingSession from './hooks/useResourceBookingSession';
import { CollectionPage, ResourcePage } from './pages';
import { MappedContinuousGroupPage } from './pages/ResourceGroupPage/MappedContinuousGroupPage';
import { MappedResourceGroupPage } from './pages/ResourceGroupPage/MappedResourceGroupPage';
import { NonMappedResourceGroupPage } from './pages/ResourceGroupPage/NonMappedResourceGroupPage';

export const ReserveCollection = () => {
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();
  const isTabletView = useIsTablet();
  const {
    isContinuous,
    isLoading,
    isLoadingListing,
    listingError,
    cleanedUrl,
    collection,
    listing,
  } = useResourceBookingSession();
  const { brand } = useAppState();
  const isMapped = listing?.isMapped ?? false;

  // A continuously booked group is reserved by picking dates (or a start and length), then one of its
  // resources, as a scheduled group is; its own page offers the picker in place of a session list.
  const MappedGroupPage = isContinuous
    ? MappedContinuousGroupPage
    : MappedResourceGroupPage;

  useEffect(() => {
    analyticsManager.setAppData({
      [ANALYTICS_PROPERTY.PageName]: `${listing?.title} | ${brand?.settings?.name}`,
      [ANALYTICS_PROPERTY.PageType]: 'Reserve product: collection page',
    });
  }, [listing?.title, brand?.settings?.name]);

  useEffect(() => {
    if (
      !isLoading &&
      cleanedUrl &&
      !isAtRoute(location, cleanedUrl) &&
      !location.pathname.includes('/addons')
    ) {
      const [pathname, search] = cleanedUrl.split('?');
      history.replace({
        pathname,
        ...(search ? { search: `?${search}` } : {}),
      });
    }
  }, [isLoading, cleanedUrl, location, history]);

  // Show 404 only after the listing itself has finished loading — not the aggregated
  // `isLoading` which flips false as soon as ANY of the four sub-queries (listing, sessions,
  // cancellation policy, first-available-date) resolves. If cancellation policy returns
  // instantly while the listing is still in-flight, the aggregate flag briefly reports
  // "loaded" with `collection` still undefined, which mounts `NotFoundExperience` for one
  // render and fires a spurious `PAGE_STOP_ERROR` → `global_click` DCR on Adobe.
  if (listingError || (!isLoadingListing && !collection)) {
    return <NotFoundExperience />;
  }

  return (
    <Wrapper>
      {listing?.title && <PageTitle translatedTitle={listing?.title} />}
      <CollectionLayout>
        <TwoColumnMapContainer>
          {!isMapped ? (
            <Switch>
              <Route
                exact={!isTabletView}
                path={match.path}
                component={CollectionPage}
              />
              {!isTabletView && (
                <Route
                  exact
                  path={`${match.path}/group/:groupId`}
                  component={NonMappedResourceGroupPage}
                />
              )}
              <Route component={NotFoundExperience} />
            </Switch>
          ) : (
            <Switch>
              <Route exact path={match.path} component={CollectionPage} />
              <Route
                exact
                path={`${match.path}/group/:groupId`}
                component={MappedGroupPage}
              />
              <Route
                exact
                path={`${match.path}/group/:groupId/resource/:resourceId`}
                component={ResourcePage}
              />
              <Route component={NotFoundExperience} />
            </Switch>
          )}
        </TwoColumnMapContainer>
      </CollectionLayout>
    </Wrapper>
  );
};
